import {
  GATES,
  GATE_STATUS,
  ITEM_BY_ID,
  ITEM_STATUS,
  ITEMS,
  READINESS_STATUS,
  SECTIONS,
  normalizeItemStatus,
} from "./catalog";

export function emptyStateMap() {
  return {};
}

export function getItemStatus(states, itemId) {
  return normalizeItemStatus(states?.[itemId]?.status);
}

export function getItemNote(states, itemId) {
  const note = states?.[itemId]?.note;
  return typeof note === "string" ? note : "";
}

export function countByStatus(items, states) {
  const counts = {
    total: items.length,
    complete: 0,
    in_progress: 0,
    blocked: 0,
    not_started: 0,
  };

  for (const item of items) {
    const status = getItemStatus(states, item.id);
    if (status === ITEM_STATUS.COMPLETE) counts.complete += 1;
    else if (status === ITEM_STATUS.IN_PROGRESS) counts.in_progress += 1;
    else if (status === ITEM_STATUS.BLOCKED) counts.blocked += 1;
    else counts.not_started += 1;
  }

  return counts;
}

export function progressPercent(counts) {
  if (!counts?.total) {
    return 0;
  }
  return Math.round((counts.complete / counts.total) * 100);
}

export function assessGate(gate, states) {
  const itemStatuses = gate.itemIds.map((itemId) => getItemStatus(states, itemId));
  const blocked = itemStatuses.some((status) => status === ITEM_STATUS.BLOCKED);
  const allComplete = itemStatuses.every((status) => status === ITEM_STATUS.COMPLETE);
  const anyStarted = itemStatuses.some(
    (status) =>
      status === ITEM_STATUS.IN_PROGRESS ||
      status === ITEM_STATUS.COMPLETE ||
      status === ITEM_STATUS.BLOCKED,
  );

  let status = GATE_STATUS.NOT_STARTED;
  if (allComplete) {
    status = GATE_STATUS.PASSED;
  } else if (blocked) {
    status = GATE_STATUS.BLOCKED;
  } else if (anyStarted) {
    status = GATE_STATUS.IN_PROGRESS;
  }

  const completeCount = itemStatuses.filter((status) => status === ITEM_STATUS.COMPLETE)
    .length;

  return {
    ...gate,
    status,
    completeCount,
    totalCount: gate.itemIds.length,
    blocked,
  };
}

export function assessGates(states) {
  return GATES.map((gate) => assessGate(gate, states));
}

export function readinessOf(gates) {
  const blocking = gates.filter((gate) => gate.status !== GATE_STATUS.PASSED);
  if (blocking.length === 0) {
    return {
      status: READINESS_STATUS.GATES_CLEAR,
      blockingGates: [],
    };
  }
  return {
    status: READINESS_STATUS.NOT_READY,
    blockingGates: blocking,
  };
}

export function sectionSummaries(states) {
  return SECTIONS.map((section) => {
    const items = ITEMS.filter((item) => item.sectionId === section.id);
    const counts = countByStatus(items, states);
    return {
      ...section,
      ...counts,
      percent: progressPercent(counts),
    };
  });
}

export function blockedItems(states) {
  return ITEMS.filter((item) => getItemStatus(states, item.id) === ITEM_STATUS.BLOCKED).map(
    (item) => ({
      ...item,
      status: ITEM_STATUS.BLOCKED,
      note: getItemNote(states, item.id),
    }),
  );
}

const NEXT_ACTION_ORDER = {
  [ITEM_STATUS.BLOCKED]: 0,
  [ITEM_STATUS.IN_PROGRESS]: 1,
  [ITEM_STATUS.NOT_STARTED]: 2,
};

export const NEXT_ACTION_LIMIT = 5;

export function nextActions(states, limit = NEXT_ACTION_LIMIT) {
  return ITEMS.filter((item) => {
    const status = getItemStatus(states, item.id);
    return status !== ITEM_STATUS.COMPLETE && status !== ITEM_STATUS.BLOCKED;
  })
    .map((item, index) => {
      const status = getItemStatus(states, item.id);
      return {
        ...item,
        status,
        note: getItemNote(states, item.id),
        rank: NEXT_ACTION_ORDER[status] ?? 3,
        catalogIndex: index,
      };
    })
    .sort((a, b) => a.rank - b.rank || a.catalogIndex - b.catalogIndex)
    .slice(0, limit);
}

function toActionItem(item, states) {
  return {
    ...item,
    status: getItemStatus(states, item.id),
    note: getItemNote(states, item.id),
  };
}

/**
 * Stable next-action slots for the open dashboard.
 * Completing an item keeps that catalog row in its slot so a second click
 * cannot land on a different task. Blocked items leave the list.
 */
export function displayedNextActions(states, slotIds, limit = NEXT_ACTION_LIMIT) {
  const ids =
    Array.isArray(slotIds) && slotIds.length
      ? slotIds
      : nextActions(states, limit).map((item) => item.id);

  return ids
    .map((id) => ITEM_BY_ID[id])
    .filter(Boolean)
    .filter((item) => getItemStatus(states, item.id) !== ITEM_STATUS.BLOCKED)
    .map((item) => toActionItem(item, states));
}

/**
 * Progress is a completion metric. Readiness is decided only by the six gates.
 * Percentage never implies "Google Ready".
 */
export function assessVisibility(states) {
  const counts = countByStatus(ITEMS, states);
  const gates = assessGates(states);
  const readiness = readinessOf(gates);
  const sections = sectionSummaries(states);
  const blocked = blockedItems(states);
  const actions = nextActions(states);

  return {
    counts,
    percent: progressPercent(counts),
    gates,
    readinessStatus: readiness.status,
    blockingGates: readiness.blockingGates,
    sections,
    blocked,
    actions,
  };
}
